import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Plane, RefreshCw, ArrowLeft, Ticket, XCircle } from 'lucide-react';
import { listFlightOrders, getFlightOrder, cancelFlightOrder } from '../../services/flightService';
import {
  airportLabel, formatDuration, formatTime, formatDate, ORDER_STATUS, CABIN_LABEL, PAX_LABEL, formatMoney,
} from './flightData';

const usePageTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

const StatusBadge = ({ status }) => {
  const meta = ORDER_STATUS[status] || { label: status, badge: 'bg-gray-100 text-gray-600 border-gray-200' };
  return <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${meta.badge}`}>{meta.label}</span>;
};

const SegmentLine = ({ seg }) => (
  <div className="flex items-start gap-3 text-sm text-gray-600">
    <Plane className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
    <p>
      <span className="font-medium text-gray-900">{seg.flight_number}</span>（{seg.airline_name_zh}）
      {seg.dep_airport} {formatTime(seg.dep_time)} → {seg.arr_airport} {formatTime(seg.arr_time)}
      <span className="text-gray-400 ml-2">{formatDuration(seg.duration_minutes)}{seg.aircraft ? ` · ${seg.aircraft}` : ''}</span>
    </p>
  </div>
);

const FlightOrdersPage = () => {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const highlight = params.get('highlight');

  const [orders, setOrders] = React.useState([]);
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [cancelling, setCancelling] = React.useState('');
  const [detailError, setDetailError] = React.useState('');

  usePageTitle(orderId ? `订单 ${orderId} | 旅报报·机票` : '我的机票订单 | 旅报报·机票');

  const loadList = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setOrders(await listFlightOrders({ limit: 50 }));
    } catch (e) {
      setError(e.message || '获取订单失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = React.useCallback(async (id) => {
    setLoading(true);
    setDetailError('');
    try {
      setDetail(await getFlightOrder(id));
    } catch (e) {
      setDetailError(e.message || '获取订单失败');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (orderId) loadDetail(orderId);
    else loadList();
  }, [orderId, loadDetail, loadList]);

  const doCancel = async (id) => {
    if (cancelling) return;
    if (!window.confirm('确定取消该订单吗？')) return;
    setCancelling(id);
    try {
      if (orderId) {
        setDetail(await cancelFlightOrder(id));
      } else {
        await cancelFlightOrder(id);
        await loadList();
      }
    } catch (e) {
      window.alert(e.message || '取消失败');
    } finally {
      setCancelling('');
    }
  };

  const inputHeader = (
    <header className="sticky top-0 z-40 backdrop-blur bg-white/80 border-b border-white/40">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="旅报报 logo" className="w-9 h-9" />
          <div className="font-extrabold text-gray-900">旅报报·机票</div>
        </Link>
        <nav className="flex items-center gap-4 text-sm text-gray-700">
          <Link to="/flight" className="hover:text-gray-900">查询机票</Link>
          <Link to="/flight/orders" className="px-4 py-2 rounded-lg bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-medium hover:from-sky-600 hover:to-cyan-600 transition-all">
            我的订单
          </Link>
        </nav>
      </div>
    </header>
  );

  // ===== 详情视图 =====
  if (orderId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 via-cyan-50 to-white">
        {inputHeader}
        <main className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/flight/orders" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="w-4 h-4" /> 返回订单列表
          </Link>
          {loading && <div className="text-center py-16 text-gray-500"><RefreshCw className="w-8 h-8 mx-auto animate-spin mb-3" />加载中…</div>}
          {detailError && <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{detailError}</div>}
          {detail && !loading && (
            <div className="space-y-6">
              {/* 摘要 */}
              <div className={`rounded-3xl bg-white border shadow-sm p-6 ${highlight === detail.order_id ? 'ring-2 ring-sky-400' : 'border-sky-100'}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="font-bold text-gray-900">订单号 {detail.order_id}</div>
                  <StatusBadge status={detail.status} />
                </div>
                <div className="space-y-2 mb-4">
                  {detail.offer_snapshot.segments.map((s, i) => <SegmentLine key={i} seg={s} />)}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-gray-400 text-xs">出发日期</p><p className="font-medium text-gray-900">{formatDate(`${detail.search.depart_date}T00:00:00`)}</p></div>
                  <div><p className="text-gray-400 text-xs">航线</p><p className="font-medium text-gray-900">{airportLabel(detail.search.origin)} → {airportLabel(detail.search.destination)}</p></div>
                  <div><p className="text-gray-400 text-xs">舱位</p><p className="font-medium text-gray-900">{CABIN_LABEL[detail.search.cabin]}</p></div>
                  <div><p className="text-gray-400 text-xs">总价</p><p className="font-black text-sky-600 text-lg">{formatMoney(detail.total_price_cny, detail.currency)}</p></div>
                </div>
                {detail.ticket_numbers?.length > 0 && (
                  <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-sm text-emerald-700 flex items-center gap-2">
                    <Ticket className="w-4 h-4" /> 票号：{detail.ticket_numbers.join('、')}
                  </div>
                )}
                {detail.status === 'pending' && (
                  <button
                    onClick={() => doCancel(detail.order_id)}
                    disabled={cancelling === detail.order_id}
                    className="mt-4 inline-flex items-center gap-1 px-5 py-2.5 rounded-xl border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-all disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" /> {cancelling === detail.order_id ? '取消中…' : '取消订单'}
                  </button>
                )}
              </div>

              {/* 状态时间线 */}
              <div className="rounded-3xl bg-white border border-sky-100 shadow-sm p-6">
                <div className="font-bold text-gray-900 mb-4">状态记录</div>
                <div className="space-y-3">
                  {detail.status_history?.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className={`w-2 h-2 rounded-full ${i === detail.status_history.length - 1 ? 'bg-sky-500 ring-4 ring-sky-100' : 'bg-gray-300'}`} />
                      <span className="text-gray-900 font-medium">{ORDER_STATUS[h.to]?.label || h.to}</span>
                      <span className="text-gray-400">{h.at ? formatTime(h.at) : ''}</span>
                      <span className="text-gray-500">{h.note} {h.actor ? `· ${h.actor}` : ''}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 乘机人 */}
              <div className="rounded-3xl bg-white border border-sky-100 shadow-sm p-6">
                <div className="font-bold text-gray-900 mb-4">乘机人（{detail.passengers.length} 位）</div>
                <div className="space-y-3">
                  {detail.passengers.map((p, i) => (
                    <div key={i} className="p-4 rounded-2xl bg-sky-50/50 border border-sky-50 text-sm">
                      <p className="font-medium text-gray-900">
                        {p.last_name_en} {p.first_name_en}
                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-white border border-sky-100 text-sky-700">{PAX_LABEL[p.ptype]}</span>
                        <span className="ml-2 text-gray-500">{p.gender === 'M' ? '男' : '女'}</span>
                      </p>
                      <p className="text-gray-500 mt-1">护照 {p.passport_no} · 有效期至 {p.passport_expiry}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-sm text-gray-500">
                  联系人：{detail.contact?.name} · {detail.contact?.phone}{detail.contact?.email ? ` · ${detail.contact.email}` : ''}
                  {detail.remark && <span className="block">备注：{detail.remark}</span>}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // ===== 列表视图 =====
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-cyan-50 to-white">
      {inputHeader}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-extrabold text-gray-900">我的机票订单</h1>
          <button onClick={loadList} disabled={loading} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> 刷新
          </button>
        </div>

        {error && <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
        {loading && orders.length === 0 && <div className="text-center py-16 text-gray-500">加载中…</div>}
        {!loading && orders.length === 0 && !error && (
          <div className="text-center py-16 text-gray-500">
            <Plane className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            暂无订单，<Link to="/flight" className="text-sky-600 hover:underline">去查询机票</Link>
          </div>
        )}

        <div className="space-y-4">
          {orders.map((o) => {
            const seg = o.offer_snapshot?.segments?.[0] || {};
            const lastSeg = o.offer_snapshot?.segments?.[o.offer_snapshot.segments.length - 1] || {};
            return (
              <Link
                key={o.order_id}
                to={`/flight/orders/${o.order_id}`}
                className={`block rounded-3xl bg-white border shadow-sm hover:shadow-lg transition-all p-5 ${highlight === o.order_id ? 'ring-2 ring-sky-400 border-sky-100' : 'border-sky-100'}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-gray-900">{seg.flight_number}</span>
                    <span className="text-sm text-gray-500">{seg.airline_name_zh}</span>
                    {o.offer_snapshot?.segments?.length > 1 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">中转</span>
                    )}
                  </div>
                  <StatusBadge status={o.status} />
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {airportLabel(o.search.origin)} → {airportLabel(o.search.destination)} ·
                  {o.search.depart_date} · {formatTime(seg.dep_time)}起飞 ·
                  {o.passengers?.length || 0} 位乘机人
                </p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-gray-400">订单号 {o.order_id} · 提交于 {o.created_at ? formatTime(o.created_at) : ''}</span>
                  <span className="font-black text-sky-600 text-lg">{formatMoney(o.total_price_cny, o.currency)}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default FlightOrdersPage;
